<script setup lang="ts">
import colourLogo from '../../brand/mifco-logo-colour.svg'
import whiteLogo from '../../brand/mifco-logo-white.svg'

/**
 * `<UiBrandMark>` — MIFCO's wordmark, correct for the ground it sits on.
 *
 * The two source files (`apps/ui/brand/`) are the client's registered mark —
 * see that directory's README. They are never recoloured and never inlined
 * with their fills rewritten to `currentColor`; this component renders both
 * as plain `<img>`s and lets CSS pick which one is visible, so the browser
 * decides using `prefers-color-scheme`/`[data-theme]` with no JavaScript and
 * no flash of the wrong variant on first paint.
 *
 * Both files share one 400x173 viewBox, so either can be the layout box:
 * `height` drives the size and width follows from the fixed aspect ratio.
 */

withDefaults(
  defineProps<{
    /** Any CSS length. Width follows from the logo's fixed 400:173 ratio. */
    height?: string | number
    /**
     * Accessible name. Omit when the mark sits beside text that already
     * names the product (nav brand row, login card) — same convention as
     * `<UiIcon>`: decorative unless it is the only content of a control.
     */
    label?: string
  }>(),
  { height: '28px' },
)
</script>

<template>
  <span
    class="ui-brand-mark"
    :style="{ height: typeof height === 'number' ? `${height}px` : height }"
    :role="label ? 'img' : undefined"
    :aria-label="label"
    :aria-hidden="label ? undefined : 'true'"
  >
    <img class="ui-brand-mark__img ui-brand-mark__img--colour" :src="colourLogo" alt="" />
    <img class="ui-brand-mark__img ui-brand-mark__img--white" :src="whiteLogo" alt="" />
  </span>
</template>

<style scoped>
.ui-brand-mark {
  display: inline-flex;
  align-items: center;
  flex: none;
}

.ui-brand-mark__img {
  display: block;
  block-size: 100%;
  inline-size: auto;
}

.ui-brand-mark__img--white {
  display: none;
}

/* Same three-state pattern as apps/ui/tokens.css: a media query for a visitor
   with no explicit preference, guarded so an explicit light choice on a dark
   OS still wins, and the attribute selector for an explicit dark choice. */
@media (prefers-color-scheme: dark) {
  :root:not([data-theme='light']) .ui-brand-mark__img--colour {
    display: none;
  }

  :root:not([data-theme='light']) .ui-brand-mark__img--white {
    display: block;
  }
}

:root[data-theme='dark'] .ui-brand-mark__img--colour {
  display: none;
}

:root[data-theme='dark'] .ui-brand-mark__img--white {
  display: block;
}
</style>
